import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  Input,
  OnInit,
  Optional
} from "@angular/core";
import { RoutingService, WindowRef } from "@spartacus/core";
import { CmsComponentData, SearchBoxComponent } from "@spartacus/storefront";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { CustomSearchBoxComponentService } from './custom-search-box-component.service';
import { CustomCmsSearchBoxComponent, CustomSearchBoxConfig } from "./custom-search-box.model";

const DEFAULT_SEARCH_BOX_CONFIG: CustomSearchBoxConfig = {
  minCharactersBeforeRequest: 1,
  displayProducts: true,
  displaySuggestions: true,
  maxProducts: 5,
  maxSuggestions: 5,
  displayProductImages: true,
  displayStaticContent: true,
};

@Component({
  selector: 'cx-searchbox',
  templateUrl: './custom-search-box.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class CustomSearchBoxComponent extends SearchBoxComponent implements OnInit {
  @Input() config: CustomSearchBoxConfig;

  /**
   * Override ignoreCloseEvent props in class SearchBoxComponent
   */
  private ignoreCustomCloseEvent = false;

  constructor(
    protected searchBoxComponentService: CustomSearchBoxComponentService,
    @Optional()
    protected componentData: CmsComponentData<CustomCmsSearchBoxComponent>,
    protected winRef: WindowRef,
    protected routingService: RoutingService,
  ) {
    super(searchBoxComponentService, componentData, winRef, routingService);
  }

  data$: Observable<CustomSearchBoxConfig> = this.componentData.data$.pipe(map(component => component));

  /**
   * Listener for clickout out of searchInput and searchPanel
   * */
  @HostListener('document:click', ['$event'])
  clickout(event: UIEvent) {
    if (!this.winRef.document.hasFocus()) {
      super.clickout(event);
    }
  }


  /**
   * Closes the type-ahead searchBox.
   */
  close(force?: boolean): void {
    // Use timeout to detect changes
    setTimeout(() => {
      if ((!this.ignoreCustomCloseEvent && !this.isCustomSearchBoxFocused()) || force) {
        this.blurSearchBox();
      }
    });
  }

  // Check if focus is on searchbox or result list elements
  private isCustomSearchBoxFocused(): boolean {
    return (
      this.winRef.document.hasFocus() && (
        this.getResultElements().includes(this.getFocusedElement()) ||
        (this.winRef.document.querySelector('input[aria-label="Search"]') ===
          this.getFocusedElement())
      )
    );
  }

  /**
   * Disables closing the search result list.
   */
  disableClose(): void {
    this.ignoreCustomCloseEvent = true;
  }

  /**
   * Clears the search box input field
   */
  clear(el: HTMLInputElement): void {
    this.disableClose();
    el.value = '';
    this.searchBoxComponentService.clearResults();

    // Use Timeout to run after blur event to prevent the searchbox from closing on mobile
    setTimeout(() => {
      // Retain focus on input lost by clicking on icon
      el.focus();
      this.ignoreCustomCloseEvent = false;
    });
  }
}
